import { ActionIcon, Group, Stack, Text, Textarea, Tooltip, UnstyledButton } from "@mantine/core";
import { Archive, Image as ImageIcon, Layers2, X } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { AiActorGender, CatalogImageModel, Job } from "../../../api/generated/model";
import { ImageActorQuote, ImageActorRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../ErrorAlert";
import type { PickedImage } from "../../media/ImagePicker";
import { ProductPicker } from "../../media/ProductPicker";
import { ReferencesDialog } from "../../media/ReferencesDialog";
import { usePricedStart } from "../../jobs/priced-start";
import { Choice } from "../../../ui/Choice";
import { cssUrl } from "../../../ui/css-url";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ModelPicker } from "../../../ui/ModelPicker";
import { Pill } from "../../../ui/Pill";
import { Price } from "../../../ui/Price";
import { GENDERS } from "../genders";
import { selectUpTo } from "../../../ui/format";
import classes from "./composer.module.css";
import { StartActions, type ComposerPlace } from "./place";

/** Live's count: one to four portraits, four at first (`ACTOR_GENERATION_MAX_IMAGES_COUNT`). */
export const MAX_ACTORS = 4;
/** Live's most products in one generation (`GENERATE_WITH_TEXT_MAX_PRODUCTS`). */
const MAX_PRODUCTS = 4;

/** A reference image, and the product it is a photo of when the Products popover gave it. */
type Reference = PickedImage & { productId?: string };

/** The models that make actors (every image model, G8-actor-generation), the default first. */
export function actorModels(models: readonly CatalogImageModel[]): CatalogImageModel[] {
  return models.filter((model) => model.job_types.includes("image.actor"));
}

export function defaultActorModel(models: readonly CatalogImageModel[]): CatalogImageModel {
  return models.find((model) => model.is_default) ?? models[0]!;
}

export interface ActorFormProps {
  teamId: string;
  brandId: string;
  models: CatalogImageModel[];
  /** The mode pills above the panel. */
  above: ReactNode;
  onStarted: (job: Job) => void;
  /** The AI Actors page, or Talking Head's "Add AI Actor". */
  place: ComposerPlace;
  /** In a card of its own, not floating (`GlassPanel`'s `inline`, the Pro Studio redesign). */
  inline?: boolean | undefined;
}

/** Live's count choice, one to the model's most; none where the place fixes the count. */
export function OutputsChoice(props: {
  place: ComposerPlace;
  most: number;
  value: number;
  onChange: (outputs: number) => void;
}) {
  if (props.place.outputs !== undefined) return null;
  return (
    <Choice
      setting="Outputs"
      icon={<Layers2 size={20} strokeWidth={1.5} />}
      width={80}
      value={String(props.value)}
      options={Array.from({ length: props.most }, (_, at) => ({
        value: String(at + 1),
        label: String(at + 1),
      }))}
      onChange={(next) => props.onChange(Number(next))}
    />
  );
}

/**
 * Live's gender cards over the form, one at a time, each with its icon (drawn in the text's
 * colour, so it reads in either theme).
 */
export function GenderPills(props: {
  value: AiActorGender | null;
  onChange: (gender: AiActorGender) => void;
}) {
  return (
    <fieldset className={classes.genders} aria-label="Gender">
      {GENDERS.map((each) => (
        <Pill
          key={each.value}
          label={each.label}
          icon={<span className={classes.genderIcon} style={{ maskImage: cssUrl(each.icon) }} aria-hidden />}
          active={props.value === each.value}
          aria-pressed={props.value === each.value}
          onClick={() => props.onChange(each.value)}
        />
      ))}
    </fieldset>
  );
}

/**
 * Live's Generate With Text (`generate-with-text`, captures 01, L04f and L04g, G8-actor-generation):
 * the gender cards; "Describe your avatar..."; at the footer's left the products (a product's
 * photo joins the references, and the product is named, up to live's four, so My Actors' Product
 * filter finds the actors saved from it, #237) and the reference images, at its right the model,
 * the count (1 to
 * 4, four at first, or the place's fixed count), the price and the start. Start waits for a
 * gender and a prompt of 10 characters, as live's form does.
 */
export function GenerateWithText(props: ActorFormProps) {
  const { teamId, brandId, models, above, onStarted, place, inline } = props;
  const [modelId, setModelId] = useState(() => defaultActorModel(models).id);
  const model = models.find((each) => each.id === modelId) ?? defaultActorModel(models);
  const cap = model.modes["image.actor"]?.references?.max ?? 0;
  // None at first on the AI Actors page, as live's; a template's own start, Female (`place.gender`).
  const [gender, setGender] = useState<AiActorGender | null>(place.gender ?? null);
  const [prompt, setPrompt] = useState("");
  const [outputs, setOutputs] = useState(Math.min(MAX_ACTORS, model.max_outputs));
  const [references, setReferences] = useState<Reference[]>([]);
  const [picking, setPicking] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  // A model with fewer references keeps the first it takes, as live's trim does.
  const kept = references.slice(0, cap);
  // The products the kept photos show, each once: a photo taken out takes its product too.
  const products = [
    ...new Set(kept.flatMap((each) => (each.productId === undefined ? [] : [each.productId]))),
  ];
  const most = Math.min(MAX_ACTORS, model.max_outputs);
  const fields = {
    type: "image.actor" as const,
    model: model.id,
    ...(gender === null ? {} : { gender }),
    prompt,
    outputs: place.outputs ?? Math.min(outputs, most),
    references: kept.map((each) => ({ kind: "asset" as const, asset_id: each.assetId })),
    product_ids: products,
  };
  // Live's bolt stays alone until the prompt has text, as the Studio's (issue 346); the quote
  // itself prices the portraits, not the words.
  const quote = prompt.trim() === "" ? null : ImageActorQuote.safeParse({ ...fields, prompt: "" });
  const request = ImageActorRequest.safeParse(fields);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote?.success === true ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  // A photo already held keeps its place; a product's photo names its product on it.
  const add = (picked: Reference[]) =>
    setReferences((current) => [
      ...current.map((had) => {
        const again = picked.find((each) => each.assetId === had.assetId);
        return again?.productId === undefined ? had : { ...had, productId: again.productId };
      }),
      ...picked.filter((each) => !current.some((had) => had.assetId === each.assetId)),
    ]);
  const full = kept.length >= cap;
  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      header={<GenderPills value={gender} onChange={setGender} />}
      summary={<span>Generate With Text</span>}
      above={above}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={8} wrap="nowrap">
            <ProductPicker
              teamId={teamId}
              brandId={brandId}
              chosen={null}
              opened={productsOpen}
              onClose={() => setProductsOpen(false)}
              onConfirm={(choice) =>
                add([
                  {
                    assetId: choice.assetId,
                    url: choice.url,
                    name: choice.name,
                    productId: choice.productId,
                  },
                ])
              }
              anchor={(wrap) =>
                wrap(
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    aria-label="Products"
                    disabled={full || products.length >= MAX_PRODUCTS}
                    onClick={() => setProductsOpen((open) => !open)}
                  >
                    <Archive size={20} strokeWidth={1.5} />
                  </ActionIcon>,
                )
              }
            />
            <Tooltip label="Reference images">
              <ActionIcon
                variant="subtle"
                size="lg"
                aria-label="Reference images"
                disabled={full}
                onClick={() => setPicking(true)}
              >
                <ImageIcon size={20} strokeWidth={1.5} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Group gap={12} wrap="nowrap">
            <ModelPicker models={models} value={model.id} onChange={setModelId} />
            <OutputsChoice
              place={place}
              most={most}
              value={Math.min(outputs, most)}
              onChange={setOutputs}
            />
            <Price priced={job.priced} pending={job.pricing} />
            <StartActions
              place={place}
              canStart={job.canStart}
              starting={job.start.isPending}
              onStart={job.startJob}
            />
          </Group>
        </Group>
      }
    >
      <Stack gap={4}>
        <Textarea
          variant="unstyled"
          aria-label="Prompt"
          placeholder="Describe your avatar..."
          autosize
          minRows={1}
          maxRows={5}
          maxLength={model.prompt_max_chars}
          value={prompt}
          onChange={(event) => setPrompt(event.currentTarget.value)}
        />
        {kept.length === 0 ? null : (
          <div className={classes.thumbs}>
            {kept.map((each) => (
              <span key={each.assetId} className={classes.thumb}>
                {each.url === null ? (
                  <Text size="xs">{each.name}</Text>
                ) : (
                  <img src={each.url} alt={each.name} className={classes.thumbPicture} />
                )}
                <UnstyledButton
                  className={classes.thumbRemove}
                  aria-label={`Remove ${each.name}`}
                  onClick={() =>
                    setReferences((current) => current.filter((had) => had.assetId !== each.assetId))
                  }
                >
                  <X size={12} strokeWidth={2.5} aria-hidden />
                </UnstyledButton>
              </span>
            ))}
          </div>
        )}
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
      </Stack>
      {picking ? (
        <ReferencesDialog
          teamId={teamId}
          brandId={brandId}
          title="Image References"
          subtitle={selectUpTo(cap - kept.length, "reference")}
          max={Math.max(1, cap - kept.length)}
          chosen={[]}
          onClose={() => setPicking(false)}
          onConfirm={add}
        />
      ) : null}
    </GlassPanel>
  );
}
